<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-primary">إدارة الحلقات العامة</h1>
        <button
          class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
          @click="openCreateModal"
        >
          <i class="fas fa-plus ml-2"></i>
          إضافة حلقة عامة
        </button>
      </div>

      <div v-if="error" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {{ error }}
      </div>
      <div v-if="successMessage" class="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
        {{ successMessage }}
      </div>

      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-4 border-b border-gray-200 flex justify-between items-center">
          <p class="text-sm text-gray-600">عدد الحلقات: {{ circles.length }}</p>
          <button
            class="px-3 py-1.5 bg-gray-100 rounded text-gray-700 hover:bg-gray-200"
            @click="loadCircles"
          >
            تحديث
          </button>
        </div>

        <div v-if="loading" class="text-center py-10 text-gray-500">جاري التحميل...</div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-secondary text-white">
              <tr>
                <th class="px-4 py-3 text-center text-xs">الحلقة</th>
                <th class="px-4 py-3 text-center text-xs">المعلم</th>
                <th class="px-4 py-3 text-center text-xs">الوقت</th>
                <th class="px-4 py-3 text-center text-xs">الأيام</th>
                <th class="px-4 py-3 text-center text-xs">السعة</th>
                <th class="px-4 py-3 text-center text-xs">الحالة</th>
                <th class="px-4 py-3 text-center text-xs">الإجراءات</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="circle in circles" :key="circle.id" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-sm font-medium text-center">{{ circle.title }}</td>
                <td class="px-4 py-3 text-sm text-center">{{ circle.teacherName || '-' }}</td>
                <td class="px-4 py-3 text-sm text-center">{{ formatTime(circle.sessionTimeFrom) }} - {{ formatTime(circle.sessionTimeTo) }}</td>
                <td class="px-4 py-3 text-sm text-center">{{ formatDays(circle.days) }}</td>
                <td class="px-4 py-3 text-sm text-center">{{ circle.registeredStudents }} / {{ circle.maxStudents }}</td>
                <td class="px-4 py-3 text-sm text-center">
                  <span
                    class="px-2 py-1 rounded-full text-xs"
                    :class="circle.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'"
                  >
                    {{ circle.isActive ? 'مفعلة' : 'غير مفعلة' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-center">
                  <div class="flex gap-3 justify-center">
                    <button class="text-blue-600 hover:text-blue-800" @click="openEditModal(circle)">تعديل</button>
                    <button class="text-primary hover:text-primary-dark" @click="openStudentsModal(circle)">إدارة الطلاب</button>
                  </div>
                </td>
              </tr>
              <tr v-if="circles.length === 0">
                <td colspan="7" class="px-4 py-6 text-center text-gray-500">لا توجد حلقات عامة</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create/Edit Circle Modal -->
    <div v-if="showCircleModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 class="text-xl font-bold text-primary mb-4">
          {{ isEditMode ? 'تعديل الحلقة العامة' : 'إضافة حلقة عامة' }}
        </h3>

        <form @submit.prevent="saveCircle" class="space-y-4">
          <div>
            <label class="block mb-1 text-sm text-gray-700">عنوان الحلقة *</label>
            <input v-model="circleForm.title" required type="text" class="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label class="block mb-1 text-sm text-gray-700">المعلم *</label>
            <select v-model="circleForm.teacherId" required class="w-full px-3 py-2 border rounded">
              <option value="">اختر المعلم</option>
              <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                {{ teacher.fullName }}
              </option>
            </select>
          </div>

          <div>
            <label class="block mb-1 text-sm text-gray-700">السعة القصوى *</label>
            <input v-model.number="circleForm.maxStudents" required min="1" type="number" class="w-full px-3 py-2 border rounded" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block mb-1 text-sm text-gray-700">من *</label>
              <input v-model="circleForm.sessionTimeFrom" required type="time" step="1" class="w-full px-3 py-2 border rounded" />
            </div>
            <div>
              <label class="block mb-1 text-sm text-gray-700">إلى *</label>
              <input v-model="circleForm.sessionTimeTo" required type="time" step="1" class="w-full px-3 py-2 border rounded" />
            </div>
          </div>

          <div>
            <label class="block mb-2 text-sm text-gray-700">أيام الحلقة</label>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
              <label
                v-for="day in dayOptions"
                :key="day"
                class="flex items-center gap-2 px-2 py-2 border rounded cursor-pointer hover:bg-gray-50"
              >
                <input
                  :value="day"
                  v-model="circleForm.days"
                  type="checkbox"
                />
                <span class="text-sm text-gray-700">{{ day }}</span>
              </label>
            </div>
          </div>

          <div>
            <label class="block mb-1 text-sm text-gray-700">Source Tracker ID</label>
            <input
              :value="selectedCircle?.sourceTrackerId || 'يتم تعيينه تلقائيا من النظام'"
              type="text"
              disabled
              class="w-full px-3 py-2 border rounded bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          <label class="inline-flex items-center gap-2">
            <input v-model="circleForm.isActive" type="checkbox" />
            <span class="text-sm text-gray-700">مفعلة</span>
          </label>

          <div class="flex justify-end gap-3 pt-2">
            <button type="button" @click="closeCircleModal" class="px-4 py-2 bg-gray-200 rounded">إلغاء</button>
            <button type="submit" :disabled="submitting" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50">
              {{ submitting ? 'جارٍ الحفظ...' : (isEditMode ? 'تحديث' : 'إنشاء') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Students Modal -->
    <div v-if="showStudentsModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-primary">إدارة طلاب الحلقة: {{ selectedCircle?.title }}</h3>
          <button @click="closeStudentsModal" class="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div class="bg-gray-50 border border-gray-200 rounded p-3 mb-4 flex items-center gap-3 flex-wrap">
          <select v-model="newStudentId" class="px-3 py-2 border rounded min-w-[220px]">
            <option value="">اختر طالب لإضافته</option>
            <option v-for="student in studentsPool" :key="student.id" :value="student.id">
              {{ student.fullName }}
            </option>
          </select>
          <button class="px-3 py-2 bg-primary text-white rounded hover:bg-primary-dark" @click="addStudentToCircle">إضافة الطالب</button>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-secondary text-white">
              <tr>
                <th class="px-3 py-2 text-right text-xs">الطالب</th>
                <th class="px-3 py-2 text-right text-xs">الحالة</th>
                <th class="px-3 py-2 text-right text-xs">انضم</th>
                <th class="px-3 py-2 text-right text-xs">خرج</th>
                <th class="px-3 py-2 text-right text-xs">نقل إلى</th>
                <th class="px-3 py-2 text-right text-xs">الإجراءات</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="student in circleStudents" :key="student.registrationId">
                <td class="px-3 py-2 text-sm">{{ student.studentName }}</td>
                <td class="px-3 py-2 text-sm">
                  <select
                    :value="student.status"
                    class="px-2 py-1 border rounded"
                    @change="onChangeStatus(student, $event.target.value)"
                  >
                    <option value="registered">registered</option>
                    <option value="joined">joined</option>
                    <option value="left">left</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td class="px-3 py-2 text-sm">{{ student.joinedAt || '-' }}</td>
                <td class="px-3 py-2 text-sm">{{ student.leftAt || '-' }}</td>
                <td class="px-3 py-2 text-sm">
                  <div class="flex gap-2 items-center">
                    <select v-model="moveTargets[student.studentId]" class="px-2 py-1 border rounded">
                      <option value="">اختر حلقة</option>
                      <option
                        v-for="circle in targetCirclesForMove"
                        :key="circle.id"
                        :value="circle.id"
                      >
                        {{ circle.title }}
                      </option>
                    </select>
                    <button class="px-2 py-1 bg-blue-600 text-white rounded" @click="moveStudent(student)">نقل</button>
                  </div>
                </td>
                <td class="px-3 py-2 text-sm">
                  <button class="text-red-600 hover:text-red-800" @click="removeStudent(student)">إزالة</button>
                </td>
              </tr>
              <tr v-if="circleStudents.length === 0">
                <td colspan="6" class="text-center px-3 py-4 text-gray-500">لا يوجد طلاب مسجلين</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import url from '@/url';
import {
  createPublicCircle,
  getPublicCircles,
  getPublicCircleStudents,
  moveStudentBetweenPublicCircles,
  registerStudentsToPublicCircle,
  removeStudentFromPublicCircle,
  updatePublicCircleStudentStatus,
  updatePublicCircle,
} from '@/services/publicCircles';

const defaultForm = () => ({
  title: '',
  teacherId: '',
  sessionTimeFrom: '',
  sessionTimeTo: '',
  maxStudents: 20,
  days: [],
  isActive: true,
});

const dayOptions = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const dayAliases = {
  sunday: 'الأحد',
  monday: 'الاثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة',
  saturday: 'السبت',
};

export default {
  name: 'PublicCirclesManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      error: '',
      successMessage: '',
      circles: [],
      teachers: [],
      studentsPool: [],
      showCircleModal: false,
      showStudentsModal: false,
      isEditMode: false,
      selectedCircle: null,
      dayOptions,
      circleForm: defaultForm(),
      circleStudents: [],
      newStudentId: '',
      moveTargets: {},
    };
  },
  computed: {
    targetCirclesForMove() {
      if (!this.selectedCircle) return this.circles;
      return this.circles.filter(circle => circle.id !== this.selectedCircle.id);
    },
  },
  async mounted() {
    await Promise.all([this.loadCircles(), this.loadTeachers(), this.loadStudentsPool()]);
  },
  methods: {
    async loadCircles() {
      this.loading = true;
      this.error = '';
      try {
        this.circles = await getPublicCircles();
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'فشل تحميل الحلقات العامة';
      } finally {
        this.loading = false;
      }
    },
    async loadTeachers() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.get_all_teachers, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        const rawTeachers = response?.data?.result?.data || response?.data?.data || [];
        this.teachers = (Array.isArray(rawTeachers) ? rawTeachers : []).map(teacher => ({
          ...teacher,
          fullName: teacher.fullName || teacher.full_name || teacher.name || `#${teacher.id}`,
        }));
      } catch (err) {
        this.teachers = [];
      }
    },
    async loadStudentsPool() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.get_enabled_students, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        const rawStudents = response?.data?.result?.data || response?.data?.data || [];
        this.studentsPool = (Array.isArray(rawStudents) ? rawStudents : []).map(student => ({
          ...student,
          fullName: student.fullName || student.full_name || student.name || `#${student.id}`,
        }));
      } catch (err) {
        this.studentsPool = [];
      }
    },
    openCreateModal() {
      this.isEditMode = false;
      this.selectedCircle = null;
      this.circleForm = defaultForm();
      this.showCircleModal = true;
    },
    openEditModal(circle) {
      this.isEditMode = true;
      this.selectedCircle = circle;
      this.circleForm = {
        title: circle.title || '',
        teacherId: circle.teacherId || '',
        sessionTimeFrom: circle.sessionTimeFrom || '',
        sessionTimeTo: circle.sessionTimeTo || '',
        maxStudents: circle.maxStudents || 20,
        days: this.normalizeDays(circle.days),
        isActive: !!circle.isActive,
      };
      this.showCircleModal = true;
    },
    closeCircleModal() {
      this.showCircleModal = false;
      this.circleForm = defaultForm();
    },
    async saveCircle() {
      this.submitting = true;
      this.error = '';
      this.successMessage = '';
      try {
        const payload = {
          title: this.circleForm.title,
          teacherId: Number(this.circleForm.teacherId),
          sessionTimeFrom: this.ensureTimeWithSeconds(this.circleForm.sessionTimeFrom),
          sessionTimeTo: this.ensureTimeWithSeconds(this.circleForm.sessionTimeTo),
          maxStudents: Number(this.circleForm.maxStudents),
          days: this.normalizeDays(this.circleForm.days),
          isActive: !!this.circleForm.isActive,
        };

        if (this.isEditMode && this.selectedCircle?.id) {
          await updatePublicCircle(this.selectedCircle.id, payload);
          this.successMessage = 'تم تحديث الحلقة بنجاح';
        } else {
          await createPublicCircle(payload);
          this.successMessage = 'تم إنشاء الحلقة بنجاح';
        }

        this.closeCircleModal();
        await this.loadCircles();
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'فشل حفظ بيانات الحلقة';
      } finally {
        this.submitting = false;
      }
    },
    async openStudentsModal(circle) {
      this.selectedCircle = circle;
      this.showStudentsModal = true;
      this.newStudentId = '';
      await this.loadCircleStudents();
    },
    closeStudentsModal() {
      this.showStudentsModal = false;
      this.selectedCircle = null;
      this.circleStudents = [];
      this.moveTargets = {};
    },
    async loadCircleStudents() {
      if (!this.selectedCircle?.id) return;
      this.error = '';
      try {
        const result = await getPublicCircleStudents(this.selectedCircle.id);
        this.circleStudents = result?.students || [];
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'فشل تحميل طلاب الحلقة';
      }
    },
    async addStudentToCircle() {
      if (!this.selectedCircle?.id || !this.newStudentId) return;
      this.error = '';
      try {
        await registerStudentsToPublicCircle(this.selectedCircle.id, [Number(this.newStudentId)], false);
        this.successMessage = 'تمت إضافة الطالب للحلقة';
        this.newStudentId = '';
        await Promise.all([this.loadCircleStudents(), this.loadCircles()]);
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'تعذر إضافة الطالب';
      }
    },
    async removeStudent(student) {
      if (!this.selectedCircle?.id || !student?.studentId) return;
      this.error = '';
      try {
        await removeStudentFromPublicCircle(this.selectedCircle.id, student.studentId);
        this.successMessage = 'تمت إزالة الطالب من الحلقة';
        await Promise.all([this.loadCircleStudents(), this.loadCircles()]);
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'تعذر إزالة الطالب';
      }
    },
    async onChangeStatus(student, status) {
      if (!this.selectedCircle?.id || !student?.studentId) return;
      this.error = '';
      try {
        await updatePublicCircleStudentStatus(this.selectedCircle.id, student.studentId, status);
        this.successMessage = 'تم تحديث حالة الطالب';
        await Promise.all([this.loadCircleStudents(), this.loadCircles()]);
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'تعذر تحديث الحالة';
      }
    },
    async moveStudent(student) {
      const toCircleId = this.moveTargets[student.studentId];
      if (!toCircleId || !this.selectedCircle?.id) return;
      this.error = '';
      try {
        await moveStudentBetweenPublicCircles({
          studentId: student.studentId,
          fromCircleId: this.selectedCircle.id,
          toCircleId: Number(toCircleId),
        });
        this.successMessage = 'تم نقل الطالب بنجاح';
        this.moveTargets[student.studentId] = '';
        await Promise.all([this.loadCircleStudents(), this.loadCircles()]);
      } catch (err) {
        this.error = err?.response?.data?.result?.mesAr || 'تعذر نقل الطالب';
      }
    },
    ensureTimeWithSeconds(value) {
      if (!value) return '';
      return value.length === 5 ? `${value}:00` : value;
    },
    formatDays(days) {
      if (!Array.isArray(days) || days.length === 0) return '-';
      return days.join('، ');
    },
    normalizeDays(days) {
      if (!Array.isArray(days)) return [];
      const normalized = days
        .map(day => String(day || '').trim())
        .filter(Boolean)
        .map(day => {
          const lower = day.toLowerCase();
          return dayAliases[lower] || day;
        })
        .filter(day => this.dayOptions.includes(day));

      return [...new Set(normalized)];
    },
    formatDate(date) {
      if (!date) return '-';
      try {
        return new Date(date).toLocaleDateString('ar-SA');
      } catch (error) {
        return date;
      }
    },
    formatTime(time) {
      if (!time) return '-';
      try {
        const raw = String(time).length === 5 ? `${time}:00` : String(time);
        const [h, m] = raw.split(':');
        const date = new Date();
        date.setHours(Number(h), Number(m), 0, 0);
        return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true });
      } catch (error) {
        return time;
      }
    },
  },
};
</script>

<style scoped>
.text-primary {
  color: #c9a06c;
}
.bg-primary {
  background-color: #c9a06c;
}
.bg-primary-dark {
  background-color: #b8956a;
}
.bg-secondary {
  background-color: #d4a574;
}
</style>

